import React, { useState } from 'react';
import axios from 'axios';
import './PalliativeCareForm.css';

const PalliativeCareForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    gender: '',
    dob: '',
    qualification: '',
    address: '',
    mobile: '',
    registration_number: '',
    designation: '',
    hospital: '',
    experience: '',
    course: 'Palliative Care Foundation Course',
    fee: 5000
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submissionData = {
        ...formData,
        gender: formData.gender.toLowerCase(), // Convert to lowercase for enum
      };

      console.log('Submitting data:', submissionData);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/applications`,
        submissionData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (response.data.success) {
        console.log('Application submitted successfully:', response.data);
        setSubmitted(true);
      } else {
        throw new Error(response.data.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error.response) {
        const errorData = error.response.data;
        if (errorData.errors) {
          const errorMessages = Object.values(errorData.errors).flat().join(', ');
          setError(`Validation failed: ${errorMessages}`);
        } else {
          setError(errorData.message || 'Failed to submit application');
        }
      } else if (error.request) {
        setError('Network error: Unable to connect to server. Please check your connection.');
      } else {
        setError(error.message || 'An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      gender: '',
      dob: '',
      qualification: '',
      address: '',
      mobile: '',
      registration_number: '',
      designation: '',
      hospital: '',
      experience: '',
      course: 'Palliative Care Foundation Course',
      fee: 5000
    });
    setError('');
  };

  if (submitted) {
    return (
      <div className="submission-success">
        <div className="success-icon">✓</div>
        <h2>Registration Submitted Successfully!</h2>
        <p>Thank you for registering for the 10 Days Palliative Care Foundation Course.</p>
        <p>We will contact you shortly with further details.</p>
        <button 
          onClick={() => setSubmitted(false)} 
          className="submit-button"
          style={{marginTop: '20px'}}
        >
          Submit Another Application
        </button>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="form-container">
        <div className="form-header">
          <div className="course-title">
            <h1>10 DAYS PALLIATIVE CARE FOUNDATION COURSE</h1>
            <h2>For Doctors</h2>
          </div>
          <div className="contact-info">
            <h3>Centre For Development Studies in Community Palliative Care (CDPC)</h3>
            <p>Palliative Care Manjeri<br />
            Substation Road, Cherani, Karuvambram<br />
            Pin 676123<br />
            Ph No: <a href="tel:9605767367">9605767367</a><br />
            Email: <a href="mailto:cdpcmanjeri@gmail.com">cdpcmanjeri@gmail.com</a></p>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} onReset={handleReset}>
          <div className="form-section personal-info">
            <h3>Personal Information</h3>
            <div className="form-group">
              <label htmlFor="name">Name <span className="required">*</span></label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email <span className="required">*</span></label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Gender <span className="required">*</span></label>
              <div className="radio-options">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="gender"
                    value="male"
                    checked={formData.gender === 'male'}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  <span className="radio-custom"></span>
                  Male
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="gender"
                    value="female"
                    checked={formData.gender === 'female'}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span className="radio-custom"></span>
                  Female
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="dob">Date of Birth <span className="required">*</span></label>
              <input
                type="date"
                id="dob"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobile">Mobile Number <span className="required">*</span></label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-section professional-info">
            <h3>Professional Information</h3>
            <div className="form-group">
              <label htmlFor="qualification">Qualification with Year <span className="required">*</span></label>
              <input
                type="text"
                id="qualification"
                name="qualification"
                value={formData.qualification}
                onChange={handleChange}
                placeholder="e.g., MBBS 2010"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="registration_number">Council Registration Number</label>
              <input
                type="text"
                id="registration_number"
                name="registration_number"
                value={formData.registration_number}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Profession <span className="required">*</span></label>
              <div className="radio-options">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="designation"
                    value="Medical Student"
                    checked={formData.designation === 'Medical Student'}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  <span className="radio-custom"></span>
                  Medical Student
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="designation"
                    value="MBBS"
                    checked={formData.designation === 'MBBS'}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span className="radio-custom"></span>
                  MBBS
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="designation"
                    value="BDS"
                    checked={formData.designation === 'BDS'}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span className="radio-custom"></span>
                  BDS
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="hospital">Hospital/Clinic Name <span className="required">*</span></label>
              <input
                type="text"
                id="hospital"
                name="hospital"
                value={formData.hospital}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="experience">Any previous training in palliative care? If yes, please specify</label>
              <textarea
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                rows={3}
                disabled={loading}
                placeholder="Describe any previous palliative care training..."
              />
            </div>
          </div>

          <div className="form-section address-info">
            <h3>Address</h3>
            <div className="form-group">
              <label htmlFor="address">Address <span className="required">*</span></label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={4}
                required
                disabled={loading}
                placeholder="Enter your complete address..."
              />
            </div>
          </div>

          <div className="form-section course-info">
            <h3>Course Information</h3>
            <div className="form-group">
              <label>Course: <strong>Palliative Care Foundation Course</strong></label>
            </div>
            <div className="form-group">
              <label>Course Fee: <strong>₹5000</strong></label>
            </div>
          </div>

          <div className="form-footer">
            <p className="privacy-notice">
              The information provided will be used solely for course registration purposes.
            </p>
            <div className="form-actions">
              <button type="reset" className="reset-button" disabled={loading}>
                Clear Form
              </button>
              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Registration'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PalliativeCareForm;